import React from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { IconArrowMerge, IconCheck, IconX } from '@tabler/icons-react';
import useMergeReservationCandidate from '../../../../services/booking/hooks/v4/useMergeReservationCandidate';
import useMergeReservation from '../../../../services/booking/hooks/v4/useMergeReservation';
import { extractErrorMessage } from '../../../../shared/utils/serverValidation';
import FormSpinner from '../../../../shared/components/spinner/FormSpinner';

interface MergeReservationModalProps {
    show: boolean;
    onClose: () => void;
    bookingId: string;
}

const MergeReservationModal: React.FC<MergeReservationModalProps> = ({ show, onClose, bookingId }) => {
    const { data: candidateResponse, isLoading: isLoadingCandidate } = useMergeReservationCandidate({
        bookingRoomId: bookingId,
        enabled: show,
    });
    const { mutate: mergeReservation, isPending: isSubmitting, error: mergeError, reset: resetMergeError } = useMergeReservation();

    const candidate = candidateResponse?.data;

    const handleClose = () => {
        resetMergeError();
        onClose();
    };

    const handleMerge = () => {
        if (!candidate || !candidate.can_merge) return;

        mergeReservation({ booking_room_id: bookingId }, {
            onSuccess: () => {
                onClose();
            }
        });
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fs-5">
                    <IconArrowMerge className="me-2 text-primary" />
                    Merge Reservation
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                {isLoadingCandidate ? (
                    <FormSpinner />
                ) : !candidate ? (
                    <Alert variant="danger" className="small mb-0">
                        Failed to load merge candidate.
                    </Alert>
                ) : (
                    <>
                        {!candidate.can_merge && (
                            <Alert variant="warning" className="small">
                                Reservations can only be merged when they meet all of the requirements below:
                            </Alert>
                        )}

                        <ul className="list-unstyled mb-3">
                            {candidate.rules.map((rule) => (
                                <li key={rule.key} className="mb-2">
                                    <div className="d-flex align-items-start">
                                        {rule.passed ? (
                                            <IconCheck size={16} className="text-success me-2 flex-shrink-0 mt-1" />
                                        ) : (
                                            <IconX size={16} className="text-danger me-2 flex-shrink-0 mt-1" />
                                        )}
                                        <span>{rule.label}</span>
                                    </div>
                                    {!rule.passed && (rule.expected != null || rule.actual != null) && (
                                        <div className="text-muted small ms-4">
                                            Segment 1: {rule.expected ?? '-'} · Segment 2: {rule.actual ?? '-'}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {/* JANGAN pakai <Alert variant="light"> di sini. Tema Tabler
                            mendefinisikan .alert-light DUA KALI, dan yang belakangan
                            menang dengan `--tblr-alert-color: var(--tblr-light)` =
                            #fcfdfe — praktis putih di atas latar yang juga nyaris
                            putih, sehingga teks tanggalnya tidak terbaca sama sekali.
                            Dipakai div biasa dengan `text-body` supaya warnanya ikut
                            warna teks normal dan tidak bergantung pada varian alert. */}
                        {candidate.can_merge && candidate.result_preview && (
                            <div className="border rounded bg-light text-body small mb-0 p-2">
                                <strong>{candidate.result_preview.checkin_date} → {candidate.result_preview.checkout_date}</strong>
                                {' '}({candidate.result_preview.nights} nights)
                            </div>
                        )}

                        {mergeError && (
                            <Alert variant="danger" className="py-2 small mt-3 mb-0">
                                {extractErrorMessage(mergeError, 'Failed to merge reservation')}
                            </Alert>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                <Button
                    variant="primary"
                    onClick={handleMerge}
                    disabled={isSubmitting || isLoadingCandidate || !candidate?.can_merge}
                >
                    {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Merge reservation'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MergeReservationModal;
